import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@repo/common/utils/cn";

/**
 *
 * Body Text
 *
 */

export const _BODY_BASE = "font-sans not-italic text-sm";
const textBodyVariants = cva(_BODY_BASE, {
  variants: {
    weight: {
      light: "font-light",
      normal: "font-normal",
      medium: "font-medium",
      bold: "font-bold"
    },
    size: {
      sm: "text-xs",
      md: "text-sm",
      lg: "text-base"
    }
  },
  defaultVariants: {
    weight: "normal",
    size: "md"
  }
});
export type TextBodyProps = React.ComponentProps<"p"> & VariantProps<typeof textBodyVariants> & {
  asChild?: boolean;
};
function TextBody(t0) {
  const $ = _c(16);
  let className;
  let props;
  let size;
  let t1;
  let weight;
  if ($[0] !== t0) {
    ({
      className,
      weight,
      size,
      asChild: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = size;
    $[4] = t1;
    $[5] = weight;
  } else {
    className = $[1];
    props = $[2];
    size = $[3];
    t1 = $[4];
    weight = $[5];
  }
  const asChild = t1 === undefined ? false : t1;
  let t2;
  if ($[6] !== className || $[7] !== size || $[8] !== weight) {
    t2 = cn(textBodyVariants({
      weight,
      size,
      className
    }));
    $[6] = className;
    $[7] = size;
    $[8] = weight;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const baseClassName = t2;
  if (asChild) {
    const t3 = props as React.ComponentPropsWithoutRef<typeof Slot>;
    let t4;
    if ($[10] !== baseClassName || $[11] !== t3) {
      t4 = <Slot data-slot="text-body" className={baseClassName} {...t3} />;
      $[10] = baseClassName;
      $[11] = t3;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    return t4;
  }
  let t3;
  if ($[13] !== baseClassName || $[14] !== props) {
    t3 = <p data-slot="text-body" className={baseClassName} {...props} />;
    $[13] = baseClassName;
    $[14] = props;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  return t3;
}

/**
 *
 * Header
 *
 */

const _HEADER_BASE = "font-sans not-italic font-bold leading-normal capitalize";
const textHeadingVariants = cva(_HEADER_BASE, {
  variants: {
    size: {
      sm: "",
      md: "text-xl",
      lg: "text-2xl",
      xl: "text-xl md:text-4xl" // not exact to henry
    }
  },
  defaultVariants: {
    size: "md"
  }
});
export type TextHeadingProps = React.ComponentProps<"h2"> & VariantProps<typeof textHeadingVariants> & {
  asChild?: boolean;
};
function TextHeading(t0) {
  const $ = _c(14);
  let className;
  let props;
  let size;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      size,
      asChild: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = size;
    $[4] = t1;
  } else {
    className = $[1];
    props = $[2];
    size = $[3];
    t1 = $[4];
  }
  const asChild = t1 === undefined ? false : t1;
  let t2;
  if ($[5] !== className || $[6] !== size) {
    t2 = cn(textHeadingVariants({
      size,
      className
    }));
    $[5] = className;
    $[6] = size;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const baseClassName = t2;
  if (asChild) {
    const t3 = props as React.ComponentPropsWithoutRef<typeof Slot>;
    let t4;
    if ($[8] !== baseClassName || $[9] !== t3) {
      t4 = <Slot data-slot="text-heading" className={baseClassName} {...t3} />;
      $[8] = baseClassName;
      $[9] = t3;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    return t4;
  }
  let t3;
  if ($[11] !== baseClassName || $[12] !== props) {
    t3 = <h2 data-slot="text-heading" className={baseClassName} {...props} />;
    $[11] = baseClassName;
    $[12] = props;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
}

/**
 *
 * Keyword
 *
 */

const _KEYWORD_BASE = "font-sans not-italic font-bold tracking-wide uppercase";
const textKeywordVariants = cva(_KEYWORD_BASE, {
  variants: {
    size: {
      sm: "text-xs",
      md: "text-sm"
    }
  },
  defaultVariants: {
    size: "md"
  }
});
export type TextKeywordProps = React.ComponentProps<"span"> & VariantProps<typeof textKeywordVariants> & {
  asChild?: boolean;
};
function TextKeyword(t0) {
  const $ = _c(14);
  let className;
  let props;
  let size;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      size,
      asChild: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = size;
    $[4] = t1;
  } else {
    className = $[1];
    props = $[2];
    size = $[3];
    t1 = $[4];
  }
  const asChild = t1 === undefined ? false : t1;
  let t2;
  if ($[5] !== className || $[6] !== size) {
    t2 = cn(textKeywordVariants({
      size,
      className
    }));
    $[5] = className;
    $[6] = size;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const baseClassName = t2;
  if (asChild) {
    const t3 = props as React.ComponentPropsWithoutRef<typeof Slot>;
    let t4;
    if ($[8] !== baseClassName || $[9] !== t3) {
      t4 = <Slot data-slot="text-keyword" className={baseClassName} {...t3} />;
      $[8] = baseClassName;
      $[9] = t3;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    return t4;
  }
  let t3;
  if ($[11] !== baseClassName || $[12] !== props) {
    t3 = <span data-slot="text-keyword" className={baseClassName} {...props} />;
    $[11] = baseClassName;
    $[12] = props;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
}
const Body = TextBody;
const Heading = TextHeading;
const Keyword = TextKeyword;
export { Body, Heading, Keyword, textBodyVariants, textHeadingVariants, textKeywordVariants };